import { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';
import { Box, Flex, VStack, useBreakpointValue } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import { ShippingOptionType } from '../../../../../bff-api/baskets/model';
import { Content } from '../../../../../components/content-asset/Content';
import Plus from '../../../../../components/icons/icon-components/plus';
import { SoftLink } from '../../../../../components/soft-link/SoftLink';
import { WishlistButton } from '../../../../../components/wishlist/WishlistButton';
import { useABTestVariable } from '../../../../../hooks/useABTestVariable';
import { useAddProductToBag } from '../../../../../hooks/useAddProductToBag';
import { useBopisStore } from '../../../../../hooks/useBopisStore';
import { useSDDStore } from '../../../../../hooks/useSDDStore';
import { ProductCarousel, Rating, Text } from '../../../../../ui-components';
import { CustomPreference } from '../../../../../utils/api/CustomPreference';
import { buildPDPUrl } from '../../../../../utils/product-utils';
import { BloomreachProductType } from '../../../hooks/use-search/use-search-types';
import { getProductTileId, mapSearchProductToProduct } from '../../../utils/search-page-utils';
import { PlpBadge } from '../../PlpBadge';
import { PlpProductPrice } from '../../PlpProductPrice';
import { PlpSwatchesList } from '../../plp-swatches-list/PlpSwatchesList';
import { useProductImages } from '../hooks/useProductImages';
import type { Store } from '../../../../../bff-api/stores/model';
import type { TextSize } from '../../../../../ui-components';
import type { PDPLocationState } from '../../../../pdp/types';
import type { SearchBadge } from '../../../hooks/use-search';
import type { SearchPrices } from '../../../hooks/use-search/use-search-types';
import type { SearchProductTileProps } from '../../search-listing/SearchListing';

interface SelectedImage {
    id: string;
    c_imageUrl: string;
}

export interface SelectedVariant {
    id: string;
    c_pwaUrl: string;
    c_imageUrl: string;
    c_badge: SearchBadge;
    searchPrices: SearchPrices;
    c_promotionHTMLs?: string[];
    sku_images?: string[];
}

export const ProductTileHardGood: FC<SearchProductTileProps> = ({
    loading,
    fetchPriority,
    onClick,
    shippingOptionType,
    source,
    ...item
}) => {
    if (item.productType !== BloomreachProductType.hardgood) {
        throw new Error('ProductTileHardGood: Product type is not hardgood');
    }
    const {
        id,
        name,
        searchPrices,
        c_imageUrl,
        c_variants,
        c_variantAttributeLink,
        c_turntoAverageRating,
        c_turntoTotalReviewCount,
        c_badge,
        c_pwaUrl,
        c_promotionHTMLs,
    } = item;

    const [activeId, setActiveId] = useState<string | undefined>(undefined);
    const [selectedImage, setSelectedImage] = useState<SelectedImage | undefined>(undefined);

    const addProductToBag = useAddProductToBag({
        ignoreInventory: true,
        openDrawer: true,
    });

    const selection: SelectedVariant = useMemo(() => {
        const foundActive = c_variants?.find((variant) => variant.id === activeId);
        if (foundActive) {
            return {
                id: foundActive.id,
                c_imageUrl: foundActive.c_imageUrl,
                c_pwaUrl: foundActive.c_pwaUrl,
                c_badge: foundActive.c_badge,
                searchPrices: foundActive.searchPrices,
                c_promotionHTMLs: foundActive.c_promotionHTMLs,
                sku_images: foundActive.sku_images ?? [],
            };
        }
        return {
            id,
            c_imageUrl,
            c_pwaUrl,
            c_badge,
            searchPrices,
            c_promotionHTMLs,
        };
    }, [activeId, c_variants, c_imageUrl, c_pwaUrl, c_badge, searchPrices, c_promotionHTMLs, id]);

    const url = buildPDPUrl(selection);
    const baseUrl: string = (selectedImage ?? selection).c_imageUrl;

    const { thumbnail, carouselImages } = useProductImages({
        id,
        name,
        c_imageUrl,
        c_variants,
        selection,
        loading,
        fetchPriority,
        baseUrl,
    });

    const handleOnSwatchMouseEnter = useCallback(
        (newId?: string) => {
            if (!newId) {
                return;
            }

            const foundActive = c_variants?.find((variant) => variant.id === newId);
            if (foundActive) {
                setSelectedImage({ id: newId, c_imageUrl: foundActive.c_imageUrl });
            }
        },
        [c_variants],
    );

    const handleOnSwatchMouseLeave = useCallback(() => {
        setSelectedImage(undefined);
    }, []);

    const isBadgeVisible = useMemo<boolean>(
        () => Boolean(selection.c_badge?.badgeLabel && selection.c_badge?.badgeShowOnPLP),
        [selection.c_badge],
    );
    const badgeColors = useMemo<{ bg: string; text: string }>(
        () => ({
            bg: selection.c_badge?.badgeStyle === 'Light' ? 'slt-pepper.200' : 'slt-pepper.800',
            text: selection.c_badge?.badgeStyle === 'Light' ? 'slt-pepper.1000' : 'white',
        }),
        [selection.c_badge],
    );

    const nameTextSize = useBreakpointValue<TextSize>({ base: 'small', md: 'base' }) ?? 'small';

    const shouldShowSwatches = c_variantAttributeLink === 'itemColor' && c_variants?.length > 1;

    const componentId = getProductTileId(selection.id);

    const { data: bopisStore } = useBopisStore();
    const { data: sddStore } = useSDDStore();

    const { addToBagShippingProps, pdpLocationState } = useMemo(() => {
        let store: Store | undefined | null;
        let _shippingOptionType = shippingOptionType ?? ShippingOptionType.SHIP_TO;

        if (shippingOptionType === ShippingOptionType.BOPIS) {
            store = bopisStore;
        }
        if (shippingOptionType === ShippingOptionType.SDD) {
            store = sddStore;
        }
        if (shippingOptionType !== ShippingOptionType.SHIP_TO && !store) {
            _shippingOptionType = ShippingOptionType.SHIP_TO;
        }
        const _addToBagShippingProps = {
            shippingOptionType: _shippingOptionType,
            ...(store ? { store } : {}),
        };
        const _pdpLocationState =
            (shippingOptionType === ShippingOptionType.BOPIS ||
                shippingOptionType === ShippingOptionType.SDD) &&
            !!store?.id
                ? ({ shippingOptionType, storeId: store.id } satisfies PDPLocationState)
                : undefined;

        return {
            addToBagShippingProps: _addToBagShippingProps,
            pdpLocationState: _pdpLocationState,
        };
    }, [bopisStore, sddStore, shippingOptionType]);

    const handleQuickAdd = useCallback(async () => {
        const searchProductData = activeId ? { ...item, ...selection } : item;

        const product = mapSearchProductToProduct(searchProductData);
        await addProductToBag.mutateAsync({
            ...addToBagShippingProps,
            source,
            product,
            quantity: 1,
        });
    }, [activeId, item, selection, addProductToBag, addToBagShippingProps, source]);

    const showQuickAddToBagButton = useABTestVariable<boolean>(
        CustomPreference.ENABLE_QUICK_ADD_TO_BAG_PLP,
        false,
    );

    return (
        <Box
            id={componentId}
            data-testid={componentId}
            padding="0px"
            h="100%"
            display="flex"
            position="relative"
            onClick={onClick}
        >
            <Flex position="absolute" top={1} right={1} zIndex={100} gap={1}>
                <WishlistButton productId={selection.id} />
            </Flex>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                p="0"
                position="relative"
                gap={3}
                h="100%"
                w="100%"
            >
                {isBadgeVisible && selection.c_badge?.badgeLabel && (
                    <Box position="absolute" top={4} left={{ base: 4, sm: 8, md: 4 }}>
                        <PlpBadge
                            text={selection.c_badge?.badgeLabel}
                            backgroundColor={badgeColors.bg}
                            textColor={badgeColors.text}
                        />
                    </Box>
                )}

                {baseUrl ? (
                    <div className="relative w-full" data-testid="product-tile-image-container">
                        <ProductCarousel
                            id={selection.id}
                            thumbnail={thumbnail}
                            images={carouselImages}
                            showNavigation
                            showPagination
                            url={{
                                pathname: url,
                                state: pdpLocationState,
                            }}
                        />
                        {showQuickAddToBagButton ? (
                            <button
                                type="button"
                                onClick={handleQuickAdd}
                                aria-label="Quick add to bag"
                                disabled={addProductToBag.isLoading}
                                className="product-tile-atb-button bottom-3 right-3 z-20 mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-[32px] bg-slt-pepper-900 px-3 py-1 md:absolute"
                            >
                                {addProductToBag.isLoading ? (
                                    <Text size="small" color="white" style="bold">
                                        <FormattedMessage id="adding" defaultMessage="Adding..." />
                                    </Text>
                                ) : (
                                    <>
                                        <Plus color="white" />

                                        <Text size="small" color="white" style="bold">
                                            <FormattedMessage id="add" defaultMessage="Add" />
                                        </Text>
                                    </>
                                )}
                            </button>
                        ) : null}
                    </div>
                ) : null}
                <div className="product-tile-info flex flex-col gap-3">
                    {shouldShowSwatches ? (
                        <PlpSwatchesList
                            currentSelection={selection}
                            variants={c_variants}
                            activeId={activeId}
                            onChange={setActiveId}
                            size="small"
                            onMouseEnter={handleOnSwatchMouseEnter}
                            onMouseLeave={handleOnSwatchMouseLeave}
                        />
                    ) : null}

                    <Flex flexDirection="column" gap={4} direction="column">
                        <Flex gap={2} direction="column">
                            <SoftLink
                                to={{
                                    pathname: url,
                                    state: pdpLocationState,
                                }}
                            >
                                <Text type="body" size={nameTextSize} style="regular" noOfLines={2}>
                                    {parse(name)}
                                </Text>
                            </SoftLink>

                            <PlpProductPrice
                                id={selection.id}
                                isSingleVariant={
                                    !activeId ? !c_variants || c_variants.length <= 1 : true
                                }
                                searchPrices={selection.searchPrices}
                            />
                        </Flex>
                        {selection.c_promotionHTMLs?.length ? (
                            <VStack spacing={1}>
                                {selection.c_promotionHTMLs.slice(0, 2).map((promotionHTML) => (
                                    <Content
                                        key={promotionHTML}
                                        serverHtml={promotionHTML}
                                        className="w-full"
                                    />
                                ))}
                            </VStack>
                        ) : null}
                        {c_turntoTotalReviewCount ? (
                            <Flex className="space-x-2" alignItems="center">
                                <Rating
                                    initialRating={
                                        c_turntoAverageRating
                                            ? parseFloat(c_turntoAverageRating)
                                            : 0
                                    }
                                    disabled
                                    iconSize="sm"
                                />
                                <Text size="small">{`(${c_turntoTotalReviewCount})`}</Text>
                            </Flex>
                        ) : null}
                    </Flex>
                </div>
            </Box>
        </Box>
    );
};
